// Find in Files (Ctrl+Shift+F) and Replace in Files (Ctrl+Shift+R): a search
// of the program's text files, in the Find tab of the pane beside the
// editor, as the Windows IDE has it in its Find tab and the Mac in its
// sidebar. Matches are found as modern/ide/find_in_files.cpp finds them: the
// text as typed, with or without its case, and as a whole word when asked,
// where a word is made of A-Z, a-z, 0-9 and _. The editor's find bar
// (editor.js) finds by the same rules.

export const WORD = /[A-Za-z0-9_]/;
// The most matches listed; Replace all still replaces them all.
const LISTED = 2000;

// "1 match", "1,200 matches" and the like.
export const counted = (count, one, many) => `${count.toLocaleString("en-US")} ${count === 1 ? one : many}`;

// The matches of query in text: [{ offset, length, row, col, line }], rows
// and columns from 1, line the text of the match's line.
export function findMatches(text, { find, matchCase = false, wholeWord = false }) {
  const matches = [];
  if (!find || find.includes("\n")) return matches;
  const source = matchCase ? text : [...text].map((character) => character.toLowerCase()).join("");
  const wanted = matchCase ? find : [...find].map((character) => character.toLowerCase()).join("");
  // Lower-casing keeps every character's length here only when it keeps the
  // whole text's; otherwise search case-sensitively rather than misplace.
  const haystack = source.length === text.length ? source : text;
  const needle = haystack === source ? wanted : find;
  let lineStart = 0, row = 1, scanned = 0;
  for (let offset = haystack.indexOf(needle); offset >= 0; offset = haystack.indexOf(needle, offset + 1)) {
    const end = offset + needle.length;
    if (wholeWord && ((offset > 0 && WORD.test(text[offset - 1])) || (end < text.length && WORD.test(text[end])))) continue;
    for (; scanned < offset; ++scanned) {
      if (text[scanned] === "\n") {
        ++row;
        lineStart = scanned + 1;
      }
    }
    let lineEnd = text.indexOf("\n", offset);
    if (lineEnd < 0) lineEnd = text.length;
    matches.push({ offset, length: needle.length, row, col: offset - lineStart + 1,
      line: text.slice(lineStart, lineEnd).replace(/\r$/, "") });
    offset = end - 1;
  }
  return matches;
}

// The text with each match replaced.
export function replaceMatches(text, matches, replacement) {
  let result = "", from = 0;
  for (const match of matches) {
    result += text.slice(from, match.offset) + replacement;
    from = match.offset + match.length;
  }
  return result + text.slice(from);
}

// The Find tab: its fields and buttons on the window's grey, and the matches
// by file under them, on the editor's blue. files(lookIn) gives the text
// files to search, [[path, text]]: "all" the program's files, "program" the
// file Run starts and the files it Includes, or "open" the files open in
// tabs. show(path, match) goes to a match. replace(changes) replaces matches,
// [{ path, matches, replacement, text }], text the file's text after, and
// gives back what failed, [{ path, message }]. ask({ title, message, detail,
// action }) asks before Replace all. report(text) repeats the tab's status
// line in the status bar, as the desktop IDEs do.
export class FindInFiles {
  constructor(element, { files, show, replace, ask, report = () => {} }) {
    this.files = files;
    this.show = show;
    this.replace = replace;
    this.ask = ask;
    this.report = report;
    this.results = [];
    this.searches = 0;
    element.innerHTML = `
      <form class="find-form">
        <input class="find-text" type="search" placeholder="Find" aria-label="Find" spellcheck="false">
        <input class="replace-text" type="text" placeholder="Replace with" aria-label="Replace with" spellcheck="false">
        <div class="find-options">
          <label><input class="match-case" type="checkbox"> Match case</label>
          <label><input class="whole-word" type="checkbox"> Whole word</label>
        </div>
        <label class="find-scope">Look in:
          <select class="look-in">
            <option value="all">All the program's files</option>
            <option value="program">The program and its Includes</option>
            <option value="open">Open files</option>
          </select></label>
        <div class="find-buttons">
          <button class="find-all" type="submit">Find all</button>
          <button class="replace-all" type="button">Replace all</button>
        </div>
        <p class="find-status" role="status"></p>
      </form>
      <ul class="tree find-results" aria-label="Matches"></ul>`;
    const $ = (selector) => element.querySelector(selector);
    this.input = $(".find-text");
    this.replacement = $(".replace-text");
    this.matchCase = $(".match-case");
    this.wholeWord = $(".whole-word");
    this.lookIn = $(".look-in");
    this.status = $(".find-status");
    this.list = $(".find-results");
    // Enter in a field is Find all, the default button.
    $(".find-form").addEventListener("submit", (event) => {
      event.preventDefault();
      if (this.input.value) this.search();
      else this.input.focus();
    });
    $(".replace-all").addEventListener("click", () => this.replaceAll());
    for (const option of [this.matchCase, this.wholeWord, this.lookIn]) option.addEventListener("change", () => {
      if (this.searched) this.search();
    });
    // The arrow keys move through the matches, and Enter goes to one.
    this.list.addEventListener("keydown", (event) => {
      const rows = [...this.list.querySelectorAll(".find-match")], at = rows.indexOf(document.activeElement);
      if (at < 0) return;
      if (event.key === "Enter") {
        event.preventDefault();
        rows[at].click();
      } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        const next = rows[Math.min(Math.max(at + (event.key === "ArrowDown" ? 1 : -1), 0), rows.length - 1)];
        rows[at].tabIndex = -1;
        next.tabIndex = 0;
        next.focus();
      }
    });
  }

  get query() {
    return { find: this.input.value, matchCase: this.matchCase.checked, wholeWord: this.wholeWord.checked };
  }

  // Ready for a search: the editor's selection, if it is on one line, to
  // find, and the focus in the Find field; or for Replace in Files in the
  // Replace field, once there is text to find.
  focus(selected = "", replacing = false) {
    if (selected && !selected.includes("\n")) this.input.value = selected;
    const field = replacing && this.input.value ? this.replacement : this.input;
    field.focus();
    field.select();
  }

  // Searches with the fields as they are. Returns false when a later search
  // took its place.
  async search() {
    const query = this.query, search = ++this.searches;
    this.searched = !!query.find;
    const results = [];
    if (query.find) {
      this.say("Searching…");
      for (const [path, text] of await this.files(this.lookIn.value)) {
        const matches = findMatches(text, query);
        if (matches.length) results.push({ path, text, matches });
      }
    }
    if (search !== this.searches) return false;
    this.results = results;
    this.found = query;
    this.render();
    return true;
  }

  get count() {
    return this.results.reduce((sum, result) => sum + result.matches.length, 0);
  }

  render() {
    const list = this.list, count = this.count;
    list.replaceChildren();
    this.say(!this.searched ? "" : !count ? `No matches for “${this.found.find}”.` :
      `${counted(count, "match", "matches")} in ${counted(this.results.length, "file", "files")}.` +
      (count > LISTED ? ` Only the first ${LISTED.toLocaleString("en-US")} are listed.` : ""));
    let listed = 0;
    for (const { path, matches } of this.results) {
      if (listed === LISTED) break;
      const header = document.createElement("li");
      header.className = "folder symbol-file";
      header.title = path;
      header.textContent = `${path} (${matches.length.toLocaleString("en-US")})`;
      list.append(header);
      for (const match of matches.slice(0, LISTED - listed)) {
        const row = document.createElement("li");
        row.className = "symbol find-match";
        row.title = `${path}, line ${match.row}`;
        row.tabIndex = listed ? -1 : 0;
        const number = document.createElement("span");
        number.className = "row";
        number.textContent = `${match.row}:`;
        const text = document.createElement("span");
        text.className = "name";
        // The line from a little before the match, so that it shows.
        const start = match.col - 1;
        const lead = match.line.slice(0, start).trimStart();
        const before = lead.length > 16 ? `…${lead.slice(-14)}` : lead;
        const mark = document.createElement("mark");
        mark.textContent = match.line.slice(start, start + match.length);
        text.append(before, mark, match.line.slice(start + match.length));
        row.append(number, " ", text);
        row.addEventListener("click", () => {
          for (const other of list.querySelectorAll(".find-match.selected")) other.classList.remove("selected");
          row.classList.add("selected");
          this.show(path, match);
        });
        list.append(row);
        ++listed;
      }
    }
  }

  // Replace all: searches with the fields as they are, asks, replaces every
  // match, and searches again, to list what the Find text matches now.
  async replaceAll() {
    if (!this.input.value) {
      this.input.focus();
      return;
    }
    const replacement = this.replacement.value;
    if (!await this.search()) return;
    const count = this.count, files = this.results.length;
    if (!count) return;
    if (!await this.ask({
      title: "Replace in Files",
      message: `Replace ${counted(count, "match", "matches")} of “${this.found.find}” with “${replacement}” in ` +
        `${counted(files, "file", "files")}?`,
      detail: "Open files change in their editors, where Undo takes the change back. Other files are saved at once.",
      action: "Replace all",
    })) return;
    const failed = await this.replace(this.results.map(({ path, text, matches }) =>
      ({ path, matches, replacement, text: replaceMatches(text, matches, replacement) })));
    const replaced = this.results.filter(({ path }) => !failed.some((failure) => failure.path === path));
    const total = replaced.reduce((sum, result) => sum + result.matches.length, 0);
    await this.search();
    this.say(`Replaced ${counted(total, "match", "matches")} in ` +
      `${counted(replaced.length, "file", "files")}.` +
      failed.map(({ path, message }) => ` Not replaced in ${path}: ${message.replace(/\.?$/, ".")}`).join(""));
  }

  // The status line, which the status bar repeats.
  say(text) {
    this.status.textContent = text;
    if (text) this.report(text);
  }
}
